import { memo, useMemo } from 'react';

import { useTrackOpenIntercomChat } from '@air/analytics';
import { Badge } from '@air/primitive-badge';
import { useAirModal } from '@air/provider-modal';
import { cn } from '@air/utils-tailwind';

import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

type StorageUpgradeCTAProps = {
  onUpgradeClick?: () => void;
};

const getTitleText = ({ isFreePlan, isOutOfStorage }: { isFreePlan: boolean; isOutOfStorage: boolean }) => {
  if (isFreePlan) {
    return "You're on the free plan";
  }
  if (!isOutOfStorage) {
    return "You're almost out of space!";
  }

  return 'Your workspace is out of storage.';
};

const getDescriptionText = ({ isFreePlan, isEnterprise }: { isFreePlan: boolean; isEnterprise: boolean }) => {
  if (isFreePlan) {
    return 'Upgrade for more storage, more users, and advanced features.';
  }
  if (isEnterprise) {
    return 'Talk to Support about upgrading your storage.';
  }

  return 'Upgrade to Enterprise for more space, custom roles, and more.';
};

export const StorageUpgradeCTA = memo(({ onUpgradeClick }: StorageUpgradeCTAProps) => {
  const { data: subscription } = useSubscriptionContext();
  const { data: storage } = useStorage();
  const isFreePlan = !!subscription?.isInFreePlan;
  const isOutOfStorage = !!storage?.isOutOfStorage;
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canEditPlan = canManageSubscription(permissions);
  const [showPlansModal] = useAirModal(PlansModal);
  const { trackOpenIntercomChat } = useTrackOpenIntercomChat();
  const isFull = !isFreePlan && isOutOfStorage;
  const isAlmostFull = !isFreePlan && !isOutOfStorage;

  const content = useMemo(
    () => (
      <>
        <div className="flex w-full">
          <p data-testid="STORAGE_UPGRADE_CTA_TITLE" className="flex-1 font-semibold text-12">
            {getTitleText({ isFreePlan, isOutOfStorage })}
          </p>
          {(isFreePlan || !isOutOfStorage) && <Badge color={isAlmostFull ? 'yellow' : 'blue'}>Upgrade</Badge>}
        </div>
        <p data-testid="STORAGE_UPGRADE_CTA_DESCRIPTION" className="mt-1 whitespace-pre-wrap text-12">
          {getDescriptionText({ isFreePlan, isEnterprise: !!subscription?.isEnterprise })}
          {!canEditPlan ? ' Contact your workspace admin to upgrade.' : ''}
        </p>
      </>
    ),
    [canEditPlan, isAlmostFull, isFreePlan, isOutOfStorage, subscription?.isEnterprise],
  );

  const parentClassNames = cn(
    'mt-3 flex flex-col rounded-4 bg-blue-3 p-2 text-left text-blue-11 hover:bg-blue-4 ui2:rounded-4 ui2:border ui2:border-grey-4 ui2:bg-grey-2 ui2:text-grey-11 ui2:hover:bg-grey-3',
    isFull && 'bg-red-3 text-red-11 hover:bg-red-4',
    isAlmostFull && 'group bg-yellow-3 text-yellow-11 hover:bg-yellow-4',
  );

  if (!canEditPlan) {
    return <div className={parentClassNames}>{content}</div>;
  }

  return (
    <button
      data-testid="STORAGE_UPGRADE_CTA_BUTTON"
      className={parentClassNames}
      onClick={() => {
        if (subscription?.isEnterprise) {
          trackOpenIntercomChat();
        } else {
          showPlansModal();
        }
        onUpgradeClick?.();
      }}
      type="button"
    >
      {content}
    </button>
  );
});

StorageUpgradeCTA.displayName = 'StorageUpgradeCTA';
